import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { supabase } from "../lib/supabase";
import { RespondHome, RespondReportHome } from "./ByInstrument";
import { PRIVACY_NOTICE_URL } from "../lib/privacyNotice";

// The respondent's whole console — PMO Self-Assessment MVP, decision §6:
// "The existing LINNFOSS consultant workspace must not be exposed to the
// respondent merely to provide access to the assessment. A dedicated
// assessment view with appropriately restricted permissions is sufficient."
//
// Two routes and nothing else. Every other address — including every
// consultant route a respondent might type or bookmark — lands on their
// assessment. None of the workspace's screens, navigation or registers is
// mounted in this tree at all, so none can leak by a missing guard.
//
// This is the presentation boundary. The data boundary is the database's
// (RLS, 0008, 0019), and holds whatever this tree renders.

export function RespondentApp({ userEmail }: { userEmail: string }) {
  return (
    <BrowserRouter>
      <RespondentShell userEmail={userEmail}>
        <Routes>
          <Route path="/respond" element={<RespondHome />} />
          <Route path="/respond/report" element={<RespondReportHome />} />
          <Route path="*" element={<Navigate to="/respond" replace />} />
        </Routes>
      </RespondentShell>
    </BrowserRouter>
  );
}

function RespondentShell({ userEmail, children }: { userEmail: string; children: ReactNode }) {
  return (
    <div style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          background: "var(--surface-nav)",
          color: "var(--text-on-dark)",
          padding: "0.75rem 1.25rem",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        {/* The white wordmark on Steel Dark, as the console sidebar (brand/README.txt). */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <img src="/brand/linnfoss-logo-light.svg" alt="LINNFOSS" style={{ height: 22, width: "auto", display: "block" }} />
          <span style={{ fontSize: "var(--lf-meta-size)", lineHeight: "var(--lf-meta-line)", fontWeight: 500, letterSpacing: "0.03em", color: "var(--text-on-dark-muted)" }}>
            Assessments
          </span>
        </div>
        <div style={{ marginLeft: "auto", fontSize: "var(--lf-meta-size)", color: "var(--text-on-dark-muted)" }}>{userEmail}</div>
        <button className="btn btn-secondary no-print" onClick={() => supabase.auth.signOut()}>
          Sign out
        </button>
      </header>
      <main style={{ flex: 1, padding: "1.5rem 1.25rem", maxWidth: 880, width: "100%", margin: "0 auto" }}>
        {children}
      </main>
      {/* G-P10: the notice is linked from the respondent screen as well as the invitation. */}
      <footer
        className="no-print"
        style={{
          padding: "0.75rem 1.25rem",
          fontSize: "var(--lf-meta-size)",
          color: "var(--text-muted)",
          borderTop: "1px solid var(--border)",
        }}
      >
        How LINNFOSS handles your personal data:{" "}
        <a href={PRIVACY_NOTICE_URL} target="_blank" rel="noopener noreferrer">
          privacy notice
        </a>
      </footer>
    </div>
  );
}
